import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useNewTranslation } from 'hooks/index'
import i18n from 'config/i18n'
import * as yup from 'yup'
import { Flex, Box, Text } from 'rebass'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, StepState, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { ActionCell } from 'components/Shared/MuiTable/Cell/ActionCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { theme } from 'theme'
import Inputs from 'components/Inputs'
import { convertToBackendStr } from 'services/dates'
import RectangleButton from 'particles/Button/RectangleButton'
import { selectSelectedCampaignSummary } from 'state/review'
import { useSelector } from 'react-redux'
import styled from 'particles/themes/styled'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { PLATFORM_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    linkInBioUrl: string
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
}

interface GetColumnsParams {
    showLinkInBio: boolean
    selectedStep: StepState | null | undefined
    isMultiSelectMode: boolean
    canManageTag: boolean
    disableApprove?: boolean
    disableRequestEdit?: boolean
    isCampaignEnded?: boolean
    task: TaskState
}

export const getColumns = ({
    showLinkInBio,
    selectedStep,
    isMultiSelectMode,
    canManageTag,
    disableApprove,
    disableRequestEdit,
    isCampaignEnded,
    task,
}: GetColumnsParams): GridColDefProps[] => {
    const hasPlatform = (platform: TASK_TYPE_ENUM) => {
        return task.type.includes(platform)
    }
    return [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
            subFields: [
                ...(hasPlatform(TASK_TYPE_ENUM.IG_POST) || hasPlatform(TASK_TYPE_ENUM.IG_REEL)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(TASK_TYPE_ENUM.FB_POST)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(TASK_TYPE_ENUM.TIKTOK_POST)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(TASK_TYPE_ENUM.XIAOHONGSHU_POST)
                    ? [
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                              headerName: i18n.t('submission.table.XHSUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        {
            field: 'url',
            headerName: i18n.t('submission.table.urlLabel'),
            width: 170,
            flex: 3,
            renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
            sortable: false,
            filterable: false,
        },
        ...(selectedStep && selectedStep.data.video_code_field_disable === false
            ? [
                  {
                      field: 'video_code',
                      headerName: i18n.t('submission.table.videoCodeLabel'),
                      width: 170,
                      flex: 3,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(showLinkInBio
            ? [
                  {
                      field: 'linkInBioUrl',
                      type: 'string',
                      headerName: i18n.t('submission.table.linkInBioLabel'),
                      width: 170,
                      flex: 3,
                      renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(!isMultiSelectMode && !isCampaignEnded
            ? [
                  {
                      field: 'actions',
                      headerName: i18n.t('submission.table.actionsLabel', 'Actions'),
                      renderCell: (props: GridRenderCellParams) => (
                          <ActionCell
                              {...props}
                              disableRequestEdit={disableRequestEdit}
                              disableApprove={disableApprove}
                          />
                      ),
                      width: 145,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]
}

export const getRows = (
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    isMultiSelectMode: boolean,
): RowsProps[] => {
    return submissions.map((submission: SubmissionState) => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.IG)[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        const xiaohongshuProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: { profile },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, xiaohongshuProfile],
            },
            url: submission.data && submission.data.url,
            video_code: submission.data && { value: submission.data.video_code },
            linkInBioUrl: linkInBioUrl,
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
            isMultiSelectMode,
        }
    })
}

export const ApproveUrlSubmission: React.FC<any> = ({ handleApprove, data, handleClear }) => {
    const [newT] = useNewTranslation()
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(newT('errorMessage.validation.enterComment', 'Please enter comment.')),
        data: yup.object({
            posting_time: yup
                .string()
                .required(
                    newT('errorMessage.validation.selectPostingTime', 'Please select the time this post went live'),
                ),
            url: yup.string().required(),
        }),
    })

    return (
        <Formik
            initialValues={{
                comment: data.comment,
                data: {
                    url: data.data.url,
                    posting_time: data.data.postingTime,
                },
            }}
            validationSchema={validation}
            enableReinitialize={true}
            onSubmit={(values: { comment: string | null; data: any }, actions): void => {
                if (campaignSummary) {
                    values.data.posting_time = convertToBackendStr(values.data.posting_time, campaignSummary.country)
                    handleApprove(data.uuid, values.comment, values.data)
                    handleClear()
                }
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <Text fontSize="0.9em" textAlign="left">
                            {newT(
                                'submission.review.selectPostingDateTime',
                                "Select the Instagram post's posting time",
                            )}
                        </Text>
                        <Inputs.Date
                            name="data.posting_time"
                            placeholderText={newT('placeholderText.selectPostingTime', 'Post Time')}
                        />
                        <Text fontSize="0.9em" textAlign="left" my={theme.spacing.xxxs}>
                            {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                        </Text>
                        <Text color={theme.colors.doveGrey} fontSize="0.6em" textAlign="left">
                            {newT('submission.review.commentsForPartiposterSubtext')}
                        </Text>
                        <div style={{ margin: '8px 0' }}>
                            <Inputs.TextArea
                                name="comment"
                                placeholder={newT('placeholderText.enterCommentForPartiposter')}
                                minTextAreaHeight={64}
                                maxTextAreaHeight={332}
                            />
                        </div>
                        <Flex>
                            <RectangleButton
                                form="outlined"
                                size="l"
                                label={newT('button.cancel')}
                                onClick={handleClear}
                            />
                            <Box mx="auto" />
                            <RectangleButton form="filled" size="l" type="submit" label={newT('button.approve')} />
                        </Flex>
                    </Form>
                )
            }}
        </Formik>
    )
}

export const ApprovePostAndUrlSubmission: React.FC<any> = ({ handleApprove, data, handleClear }) => {
    const [newT] = useNewTranslation()
    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(newT('errorMessage.validation.enterComment', 'Please enter comment.')),
        data: yup.object({
            url: yup.string().required(),
        }),
    })

    return (
        <Formik
            initialValues={{
                comment: data.comment,
                data: {
                    url: data.data.url,
                    video_code: data.data.video_code,
                },
            }}
            validationSchema={validation}
            enableReinitialize={true}
            onSubmit={(values: { comment: string | null; data: any }, actions): void => {
                handleApprove(data.uuid, values.comment, values.data)
                handleClear()
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <SubmissionContainer>
                            <Text fontSize="0.9em" textAlign="left">
                                {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                            </Text>
                            <Text color={theme.colors.doveGrey} fontSize="0.6em" textAlign="left">
                                {newT('submission.review.commentsForPartiposterSubtext')}
                            </Text>
                            <Inputs.TextArea
                                name="comment"
                                placeholder={newT('placeholderText.enterCommentForPartiposter')}
                                minTextAreaHeight={64}
                                maxTextAreaHeight={332}
                            />

                            <Flex>
                                <RectangleButton
                                    form="outlined"
                                    size="l"
                                    label={newT('button.cancel')}
                                    onClick={handleClear}
                                />
                                <Box mx="auto" />
                                <RectangleButton form="filled" size="l" type="submit" label={newT('button.approve')} />
                            </Flex>
                        </SubmissionContainer>
                    </Form>
                )
            }}
        </Formik>
    )
}

const SubmissionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    > *:last-child {
        margin: 4px 0;
    }
`
